import type { OptionItemStoryblok, RichtextStoryblok } from '@/types/component-types-sb';
import { useMemo } from 'react';

// Minimal UI option shape for fields consuming external datasource options
export interface UIOption {
  _uid?: string;
  value: string;
  label: string | RichtextStoryblok;
}

// Inline options mapping helper
export const mapInlineOption = (opt: OptionItemStoryblok): UIOption => ({
  _uid: opt._uid,
  value: opt.value,
  label: opt.label,
});

/**
 * Normalize various external payload shapes into UIOption[]
 * Supported shapes:
 * - string[] -> value/label = string
 * - { value, label }[] or { value, name }[]
 * - wrapper objects with { options | data | items }: []
 * - array of single-key maps: [{ "id": "Label" }, ...]
 * - root object map: { id1: "Label1", id2: "Label2" }
 */
export const normalizeExternalOptions = (payload: unknown): UIOption[] => {
  const toUI = (item: unknown, idx: number): UIOption | null => {
    if (typeof item === 'string') {
      return { value: item, label: item, _uid: String(idx) };
    }
    if (typeof item === 'object' && item !== null) {
      const anyItem = item as { value?: unknown; label?: unknown; name?: unknown; _uid?: unknown; id?: unknown };
      const value = typeof anyItem.value === 'string' ? anyItem.value : undefined;
      const labelRaw =
        typeof anyItem.label === 'string' ? anyItem.label : typeof anyItem.name === 'string' ? anyItem.name : undefined;
      if (value && (labelRaw || labelRaw === '')) {
        return {
          value,
          label: labelRaw ?? '',
          _uid:
            typeof anyItem._uid === 'string'
              ? anyItem._uid
              : typeof anyItem.id === 'number'
                ? String(anyItem.id)
                : undefined,
        };
      }
      // Handle single-key map objects: { "<id>": "<label>" }
      const rec = item as Record<string, unknown>;
      const keys = Object.keys(rec);
      if (keys.length === 1) {
        const k = keys[0] as string;
        const v = rec[k];
        if (typeof v === 'string') {
          return { value: k, label: v, _uid: String(idx) };
        }
      }
    }
    return null;
  };

  if (Array.isArray(payload)) {
    return payload.map(toUI).filter((v): v is UIOption => v !== null);
  }
  if (payload && typeof payload === 'object') {
    const obj = payload as Record<string, unknown>;
    const arrCandidates = [obj.options, obj.data, obj.items];
    for (const candidate of arrCandidates) {
      if (Array.isArray(candidate)) {
        return candidate.map(toUI).filter((v): v is UIOption => v !== null);
      }
    }
    // Root object map fallback: { id: "Label" }
    const entries = Object.entries(obj).filter(([, v]) => typeof v === 'string') as Array<[string, string]>;
    if (entries.length > 0) {
      return entries.map(([k, v], i) => ({ value: k, label: v, _uid: String(i) }));
    }
  }
  return [];
};
